import { FirebaseAnalytics, ConsentType, ConsentStatus} from '@capacitor-firebase/analytics';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { FacebookEvents } from 'capacitor-facebook-events';
import { initMetaPixel } from '../utils/metaPixel';
import { getPlatform, isAndroid, isIOS, isNativePlatform, isWeb } from '../utils/platformUtil';
import { getAdsTrackingData } from '../utils/adsTracking';
import TagManager from 'react-gtm-module';

interface AnalyticsUserProperties {
  [key: string]: any
}

const analyticsUserProperties: AnalyticsUserProperties = {};

export const analyticsSetUserProperties = async (properties: AnalyticsUserProperties) => {
  try {
    for(let [key, value] of Object.entries(properties)){
      if(!analyticsUserProperties[key] || analyticsUserProperties[key] !== value){
        await FirebaseAnalytics.setUserProperty({key, value});
        analyticsUserProperties[key] = value;
      }
    }
  } catch (error) {
    console.log('err', error);
  }
}

export const analyticsSetUser = async (userId: string | null) => {
  try {
    await FirebaseAnalytics.setUserId({
      userId: userId || null
    });
  } catch (e) {
    console.error("Failed to set Analytics User ID", e);
  }
};

export const initAnalytics = async () => {
  if (isIOS()) {
    const { status } = await AppTrackingTransparency.requestPermission();
    const authorized = status === 'authorized';
    
    await FirebaseAnalytics.setConsent({type: ConsentType.AnalyticsStorage, status: ConsentStatus.Granted});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdStorage, status: authorized ? ConsentStatus.Granted : ConsentStatus.Denied});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdUserData, status: authorized ? ConsentStatus.Granted : ConsentStatus.Denied});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdPersonalization, status: authorized ? ConsentStatus.Granted : ConsentStatus.Denied});

    // await FirebaseAnalytics.setEnabled({ enabled: status === 'authorized' });
    await FacebookEvents.setAdvertiserTrackingEnabled({ enabled: authorized });
  } else {
    // await FirebaseAnalytics.setEnabled({ enabled: true });
    await FirebaseAnalytics.setConsent({type: ConsentType.AnalyticsStorage, status: ConsentStatus.Granted});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdStorage, status: ConsentStatus.Granted});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdUserData, status: ConsentStatus.Granted});
    await FirebaseAnalytics.setConsent({type: ConsentType.AdPersonalization, status: ConsentStatus.Granted});

    if (isAndroid()) {
      await FacebookEvents.setAdvertiserTrackingEnabled({ enabled: true });
    } else {
      TagManager.initialize({ gtmId: 'GTM-NN7WKJTB' });
      TagManager.dataLayer({
        dataLayer: {
          event: 'consent_update',
          ad_storage: 'granted',
          analytics_storage: 'granted'
        }
      });
      try {
        await initMetaPixel();
      } catch (error) {
        console.log('facebook events init failed!');
      }
    }

    
  }
};

type AnalyticsParams = Record<string, any>;

export const analyticsLog = async (event: string, params?: AnalyticsParams) => {
  const platform = getPlatform();
  const adsTrackingData = await getAdsTrackingData();
  console.log("adsTrackingData", adsTrackingData);
  try {
    await FirebaseAnalytics.logEvent({
      name: event.replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40),
      params: {
        platform: getPlatform(),
        ...(adsTrackingData ? {
          ...(adsTrackingData.gclid ? {gclid: adsTrackingData.gclid} : {}),
          ...(adsTrackingData.utm ?? {})
        } : {}),
        ...(params ?? {})
      }
    });
  } catch (e) {
    console.error(`[${platform}]Firebase Analytics log failed`, e);
  }

  if (isWeb()) {
    TagManager.dataLayer({
      dataLayer: {
        event: event.replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40),
        ...(params ?? {})
      }
    });
  }

  try {
    const fbEventParams = {
      platform: getPlatform(),
      ...(adsTrackingData ? {
        ...(adsTrackingData.fb_ad_id ? {fb_ad_id: adsTrackingData.fb_ad_id} : {}),
        ...(adsTrackingData.utm ?? {})
      } : {}),
      ...(params ?? {})
    };
    if (isNativePlatform()) {
      await FacebookEvents.logEvent({
        event: event.replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40),
        params: fbEventParams
      });
    } else {
      if ((window as any).fbq) {
        if(event !== 'page_view'){
          (window as any).fbq('trackCustom', event, fbEventParams);
        }
      } else {
        console.error('Facebook Events web sdk not intitialized!');
      }
    }
  } catch (e) {
    console.error(`[${platform}]Facebook Analytics log failed`, e);
  }
};